import React from 'react';
import { formatActivityLogs } from '../../../lib/activityLogs';
import type { IActivityLogProps } from './activityLogs.types';

/**
 * Prepares the study's activity log entries for the activity logs panel. Memoizes formatActivityLogs
 * over the raw logs, defaulting missing logs, timezone and studyTimeOffset to empty or zero values.
 *
 * @param props - Raw studyLogs plus the study timezone and studyTimeOffset.
 * @returns An object carrying the formatted log entries.
 */
export const useActivityLogs = ({
  studyLogs,
  timezone,
  studyTimeOffset,
  startStudyDate,
  facilityCountry,
  facilityTimezone,
}: Pick<
  IActivityLogProps,
  | 'studyLogs'
  | 'timezone'
  | 'studyTimeOffset'
  | 'startStudyDate'
  | 'facilityCountry'
  | 'facilityTimezone'
>) => {
  /**
   * The study activity log entries prepared for display. Memoized over studyLogs, running formatActivityLogs with the raw logs plus the study timezone and studyTimeOffset.
   *
   * @returns Formatted activity log entries.
   */
  const logs = React.useMemo(
    () =>
      formatActivityLogs({
        logs: studyLogs ?? [],
        timezone: timezone ?? '',
        studyTimeOffset: studyTimeOffset ?? 0,
        startStudyDate,
        facilityCountry,
        facilityTimezone,
      }),
    [studyLogs],
  );

  return { logs };
};
